import { salesPointConfigurationSelector } from '@/features/configuration/configurationSelector';
import { useSelector } from '@/store/utils';
import type {
  CustomerProfileDataFieldDTO,
  PersonDataFieldDTO,
} from '@/types/dto/agent';
import { useMemo } from 'react';

export enum PASSENGER_DATA_FIELD {
  FIRST_NAME = 'PASSENGER_DATA_FIELD.FIRST_NAME',
  LAST_NAME = 'PASSENGER_DATA_FIELD.LAST_NAME',
  EMAIL = 'PASSENGER_DATA_FIELD.EMAIL',
  PHONE = 'PASSENGER_DATA_FIELD.PHONE',
  DATE_OF_BIRTH = 'PASSENGER_DATA_FIELD.DATE_OF_BIRTH',
  GENDER = 'PASSENGER_DATA_FIELD.GENDER',
  ZIP_CODE = 'PASSENGER_DATA_FIELD.ZIP_CODE',
  STATE_CODE = 'PASSENGER_DATA_FIELD.STATE_CODE',
  COMPANY_UNIT_CODE = 'PASSENGER_DATA_FIELD.COMPANY_UNIT_CODE',
}

export enum PURCHASER_DATA_FIELD {
  FIRST_NAME = 'PURCHASER_DATA_FIELD.FIRST_NAME',
  LAST_NAME = 'PURCHASER_DATA_FIELD.LAST_NAME',
  EMAIL = 'PURCHASER_DATA_FIELD.EMAIL',
  PHONE = 'PURCHASER_DATA_FIELD.PHONE',
  POSTAL_ADDRESS = 'PURCHASER_DATA_FIELD.POSTAL_ADDRESS',
}

export enum CUSTOMER_PROFILE_DATA_FIELD {
  FIRST_NAME = 'CUSTOMER_PROFILE_DATA_FIELD.FIRST_NAME',
  LAST_NAME = 'CUSTOMER_PROFILE_DATA_FIELD.LAST_NAME',
  PHONE = 'CUSTOMER_PROFILE_DATA_FIELD.PHONE',
  DATE_OF_BIRTH = 'CUSTOMER_PROFILE_DATA_FIELD.DATE_OF_BIRTH',
  GENDER = 'CUSTOMER_PROFILE_DATA_FIELD.GENDER',
  POSTAL_COUNTRY = 'CUSTOMER_PROFILE_DATA_FIELD.POSTAL_COUNTRY',
  POSTAL_ADDRESS = 'CUSTOMER_PROFILE_DATA_FIELD.POSTAL_ADDRESS',
  POSTAL_CITY = 'CUSTOMER_PROFILE_DATA_FIELD.POSTAL_CITY',
  POSTAL_CODE = 'CUSTOMER_PROFILE_DATA_FIELD.POSTAL_CODE',
}

// Configured customer-profile fields the webshop deliberately handles outside
// the config-driven form loop, so they are skipped without a dev warning:
// email is always shown read-only, password lives on the change-password page,
// and the phone area code is folded into the phone number input.
export const CUSTOMER_PROFILE_IGNORED_FIELDS: ReadonlyArray<string> = [
  'CUSTOMER_PROFILE_DATA_FIELD.EMAIL',
  'CUSTOMER_PROFILE_DATA_FIELD.PASSWORD',
  'CUSTOMER_PROFILE_DATA_FIELD.PHONE_AREA_CODE',
];

export enum VISIBILITY_OPTION {
  DISPLAYED = 'VISIBILITY_OPTION.DISPLAYED',
  MANDATORY = 'VISIBILITY_OPTION.MANDATORY',
  NOT_DISPLAYED = 'VISIBILITY_OPTION.NOT_DISPLAYED',
}

export const PASSENGER_FIELD_NAME_MAP: Record<
  PASSENGER_DATA_FIELD,
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phoneNumber'
  | 'birthDate'
  | 'gender'
  | 'zipCode'
  | 'stateCode'
  | 'companyUnitCode'
> = {
  [PASSENGER_DATA_FIELD.FIRST_NAME]: 'firstName',
  [PASSENGER_DATA_FIELD.LAST_NAME]: 'lastName',
  [PASSENGER_DATA_FIELD.EMAIL]: 'email',
  [PASSENGER_DATA_FIELD.PHONE]: 'phoneNumber',
  [PASSENGER_DATA_FIELD.DATE_OF_BIRTH]: 'birthDate',
  [PASSENGER_DATA_FIELD.GENDER]: 'gender',
  [PASSENGER_DATA_FIELD.ZIP_CODE]: 'zipCode',
  [PASSENGER_DATA_FIELD.STATE_CODE]: 'stateCode',
  [PASSENGER_DATA_FIELD.COMPANY_UNIT_CODE]: 'companyUnitCode',
};

// Backend PASSENGER_DATA_FIELD ids the webshop intentionally does not render:
// the checkout passenger form supports a defined subset, and these fields have
// no input/payload mapping (PASSENGER_NAME has no slot in the passenger update
// contract at all). Listed so they are skipped without a warning, while a
// genuinely new/unknown id still warns.
export const PASSENGER_IGNORED_FIELDS: ReadonlyArray<string> = [
  'PASSENGER_DATA_FIELD.PASSENGER_NAME',
  'PASSENGER_DATA_FIELD.NATIONALITY',
  'PASSENGER_DATA_FIELD.CITIZENSHIP',
  'PASSENGER_DATA_FIELD.DOCUMENT',
  'PASSENGER_DATA_FIELD.DOCUMENT_EXPIRATION_DATE',
  'PASSENGER_DATA_FIELD.VISA_CHECKED',
  'PASSENGER_DATA_FIELD.FATHER_NAME',
  'PASSENGER_DATA_FIELD.COMMENT',
  'PASSENGER_DATA_FIELD.CORPORATE_CARD',
  'PASSENGER_DATA_FIELD.LOYALTY_CARD',
  'PASSENGER_DATA_FIELD.TRAVEL_ACCOUNT_CARD',
  'PASSENGER_DATA_FIELD.EMERGENCY_CONTACT_NAME',
  'PASSENGER_DATA_FIELD.EMERGENCY_CONTACT_NUMBER',
  'PASSENGER_DATA_FIELD.EMERGENCY_CONTACT_ADDITIONAL_NUMBER',
  'PASSENGER_DATA_FIELD.EMERGENCY_CONTACT_EMAIL',
  'PASSENGER_DATA_FIELD.EMERGENCY_CONTACT_RELATIONSHIP',
];

export const PURCHASER_FIELD_NAME_MAP: Record<
  PURCHASER_DATA_FIELD,
  'firstName' | 'lastName' | 'email' | 'phoneNumber' | 'streetName'
> = {
  [PURCHASER_DATA_FIELD.FIRST_NAME]: 'firstName',
  [PURCHASER_DATA_FIELD.LAST_NAME]: 'lastName',
  [PURCHASER_DATA_FIELD.EMAIL]: 'email',
  [PURCHASER_DATA_FIELD.PHONE]: 'phoneNumber',
  [PURCHASER_DATA_FIELD.POSTAL_ADDRESS]: 'streetName',
};

// Customer profile form field names mirror the profile update payload, so they
// differ from the passenger/purchaser names (e.g. `genderId`/`countryId`).
export const CUSTOMER_PROFILE_FIELD_NAME_MAP: Record<
  CUSTOMER_PROFILE_DATA_FIELD,
  | 'firstName'
  | 'lastName'
  | 'phoneNumber'
  | 'birthDate'
  | 'genderId'
  | 'countryId'
  | 'streetAddress'
  | 'city'
  | 'zipCode'
> = {
  [CUSTOMER_PROFILE_DATA_FIELD.FIRST_NAME]: 'firstName',
  [CUSTOMER_PROFILE_DATA_FIELD.LAST_NAME]: 'lastName',
  [CUSTOMER_PROFILE_DATA_FIELD.PHONE]: 'phoneNumber',
  [CUSTOMER_PROFILE_DATA_FIELD.DATE_OF_BIRTH]: 'birthDate',
  [CUSTOMER_PROFILE_DATA_FIELD.GENDER]: 'genderId',
  [CUSTOMER_PROFILE_DATA_FIELD.POSTAL_COUNTRY]: 'countryId',
  [CUSTOMER_PROFILE_DATA_FIELD.POSTAL_ADDRESS]: 'streetAddress',
  [CUSTOMER_PROFILE_DATA_FIELD.POSTAL_CITY]: 'city',
  [CUSTOMER_PROFILE_DATA_FIELD.POSTAL_CODE]: 'zipCode',
};

export type PassengerFieldNames =
  (typeof PASSENGER_FIELD_NAME_MAP)[keyof typeof PASSENGER_FIELD_NAME_MAP];
export type PurchaserFieldNames =
  (typeof PURCHASER_FIELD_NAME_MAP)[keyof typeof PURCHASER_FIELD_NAME_MAP];
export type CustomerProfileFieldNames =
  (typeof CUSTOMER_PROFILE_FIELD_NAME_MAP)[keyof typeof CUSTOMER_PROFILE_FIELD_NAME_MAP];

export interface DataField<TName extends string> {
  name: TName;
  isRequired: boolean;
}

export type PersonDataField = DataField<
  PassengerFieldNames | PurchaserFieldNames
>;
export type CustomerProfileField = DataField<CustomerProfileFieldNames>;

type ConfigDataFieldDTO = PersonDataFieldDTO | CustomerProfileDataFieldDTO;

/**
 * Maps a configuration data field array into `{ name, isRequired }` entries,
 * preserving the configuration payload order. `NOT_DISPLAYED` (and fields with
 * no visibility) are dropped silently. A field that is `DISPLAYED`/`MANDATORY`
 * but has no entry in the supplied field-name map is dropped with a dev warning
 * rather than silently, so a backend field the webshop cannot yet represent
 * surfaces instead of vanishing. `isRequired` is true only for `MANDATORY`
 * fields.
 */
export const processFields = <TName extends string>(
  fields: Array<ConfigDataFieldDTO> = [],
  fieldNameMap: Record<string, TName>,
  ignoredFieldIds: ReadonlyArray<string> = []
): Array<DataField<TName>> => {
  const mapped: Array<DataField<TName>> = [];

  for (const field of fields) {
    const visibilityId = field.visibilityOptionType?.id;
    const dataFieldId = field.dataField?.id;

    const isVisible =
      visibilityId === VISIBILITY_OPTION.DISPLAYED ||
      visibilityId === VISIBILITY_OPTION.MANDATORY;

    if (!isVisible || !dataFieldId) {
      continue;
    }

    if (!(dataFieldId in fieldNameMap)) {
      if (import.meta.env.DEV && !ignoredFieldIds.includes(dataFieldId)) {
        console.warn(
          `[personFieldRenderer] Ignoring configured data field with no webshop mapping: ${dataFieldId}`
        );
      }

      continue;
    }

    mapped.push({
      name: fieldNameMap[dataFieldId],
      isRequired: visibilityId === VISIBILITY_OPTION.MANDATORY,
    });
  }

  return mapped;
};

export const usePersonFieldRenderer = () => {
  const posConfiguration = useSelector(salesPointConfigurationSelector);

  return useMemo(
    () => ({
      passengerAdmissionFields: processFields(
        posConfiguration?.passengerAdmissionDataFields,
        PASSENGER_FIELD_NAME_MAP,
        PASSENGER_IGNORED_FIELDS
      ),
      passengerTravelPassFields: processFields(
        posConfiguration?.passengerTravelPassDataFields,
        PASSENGER_FIELD_NAME_MAP,
        PASSENGER_IGNORED_FIELDS
      ),
      purchaserFields: processFields(
        posConfiguration?.purchaserDataFields,
        PURCHASER_FIELD_NAME_MAP
      ),
    }),
    [posConfiguration]
  );
};

export const useCustomerProfileFieldRenderer = () => {
  const posConfiguration = useSelector(salesPointConfigurationSelector);

  return useMemo(
    () =>
      processFields(
        posConfiguration?.customerProfileDataFields,
        CUSTOMER_PROFILE_FIELD_NAME_MAP,
        CUSTOMER_PROFILE_IGNORED_FIELDS
      ),
    [posConfiguration]
  );
};
